import { Input, InputProps, InputRef } from "antd";
import React, { useEffect, useState } from "react";
import { PasswordStrength } from 'tai-password-strength';

const PASSWORD_STRENGTH = new PasswordStrength();
const PASSWORD_STRENGTH_CODE = ['VERY_WEAK', 'WEAK', 'REASONABLE', 'STRONG', 'VERY_STRONG'];
export const defaultSettings = {
    colorScheme: {
        levels: ["#ff4033", "#fe940d", "#ffd908", "#cbe11d", "#6ecc3a"],
        noLevel: "lightgrey"
    },
    height: 4,
    space: 4,
    alwaysVisible: true
}

interface LevelChangeProps {
    onLevelChange?: (newLevel: number) => void;
}

export interface PasswordInputProps extends LevelChangeProps {
    settings?: PasswordInputSettings;
}

export const PasswordInput = React.forwardRef(({
    settings = defaultSettings,
    onLevelChange,
    value,
    onChange,
    ...props
}: PasswordInputProps & Partial<InputProps>, ref: React.Ref<InputRef>) => {
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState('');
    const inputValue = isControlled ? String(value ?? '') : internalValue;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isControlled) {
            setInternalValue(e.target.value);
        }
        onChange?.(e);
    };

    return (
        <>
            <Input.Password
                {...props}
                ref={ref}
                style={{ marginBottom: settings.space + "px", ...props.style }}
                value={inputValue}
                onChange={handleChange}
            />
            <PasswordStrengthIndicator
                input={inputValue}
                onLevelChange={onLevelChange}
                settings={settings}
            />
        </>
    );
})

interface PasswordStrengthIndicatorProps extends LevelChangeProps {
    input: string;
    settings: PasswordInputSettings;
}

export const PasswordStrengthIndicator = ({ input, settings, onLevelChange }: PasswordStrengthIndicatorProps) => {
    // Calculate level
    const level = React.useMemo(() => {
        return input.length === 0 ? -1 : PASSWORD_STRENGTH_CODE.indexOf(PASSWORD_STRENGTH.check(input).strengthCode);
    }, [input])

    // Calculate indicators
    const indicators: React.ReactElement[] = React.useMemo(() => {
        const ind = [];
        for (let i = 0; i < 5; i++) {
            const color =
                i <= level
                    ? settings.colorScheme.levels[level]
                    : settings.colorScheme.noLevel;
            ind.push(<div key={`indicator-${i}`} style={getIndicatorStyle(color, settings.height)} />);
        }

        return ind;
    }, [level, settings])

    useEffect(() => {
        onLevelChange?.(level);
    }, [onLevelChange, level])

    if (!settings.alwaysVisible && level < 0) {
        return null;
    }

    return <div style={getWrapperStyle(settings.height)}>{indicators}</div>;
};

function getWrapperStyle(height: number) {
    return {
        lineHeight: height + "px"
    };
}

function getIndicatorStyle(color: string, height: number) {
    return {
        display: "inline-block",
        width: "20%",
        backgroundColor: color,
        height: height + "px",
        borderRadius: "2px"
    };
}

export interface PasswordInputSettings {
    colorScheme: ColorScheme;
    height: number;
    alwaysVisible: boolean;
    space: number;
}

export interface ColorScheme {
    levels: string[],
    noLevel: string;
}